import { Box, Button, Center, Container, Group, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Unit } from '@/api/generated/models';
import { useListUnits, useSearchUnits } from '@/api/generated/units/units';
import {
  CardSection,
  ListPagination,
  MobileFab,
  PageHeading,
  SearchField,
  StatusBanner,
  StatusPill,
  tableRowStyle
} from '@/components/common';
import { UnitActionsMenu } from '@/features/admin/components/UnitActionsMenu';
import { UnitFormModal } from '@/features/admin/components/UnitFormModal';
import { SUPPORTED_LANGUAGES } from '@/i18n/supportedLanguages';
import { usePageSize } from '@/utils/usePageSize';

/**
 * Platform admin — globalni šifarnik jedinica rada (Figma 4525:15165). Stupci
 * za NAZIV se generiraju iz SUPPORTED_LANGUAGES, ne fiksno EN/DE/HR — nova
 * jedinica jezika u aplikaciji ovdje automatski dobije svoj stupac.
 */
export const AdminUnitsPage = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [modalOpened, setModalOpened] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [editingUnit, setEditingUnit] = useState<Unit | undefined>(undefined);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  const { data, isLoading } = useSearchUnits({
    page,
    pageSize,
    search: debouncedSearch.trim() || undefined
  });
  const pageItems = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Puna lista (bez paginacije) samo za provjeru zauzetih šifri u
  // UnitFormModalu — pretraga za prikaz liste ide preko /search iznad.
  const { data: allUnitsData } = useListUnits();
  const units: Unit[] = allUnitsData ?? [];

  const paginationLabel = t('adminUnits.showing', {
    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, totalElements),
    total: totalElements
  });

  const openCreate = () => {
    setEditingUnit(undefined);
    setModalOpened(true);
  };

  const openEdit = (unit: Unit) => {
    setEditingUnit(unit);
    setModalOpened(true);
  };

  const closeModal = () => {
    setModalOpened(false);
    setEditingUnit(undefined);
  };

  const codeColumnWidth = 140;
  const nameColumnWidth = 160;
  const actionColumnWidth = 100;

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        <Group justify="space-between" wrap="wrap" align="flex-start" visibleFrom="sm">
          <PageHeading title={t('adminUnits.title')} subtitle={t('adminUnits.subtitle')} />
          <Button onClick={openCreate}>+ {t('adminUnits.newUnit')}</Button>
        </Group>

        <Stack gap={12} hiddenFrom="sm">
          <PageHeading title={t('adminUnits.title')} subtitle={t('adminUnits.subtitle')} />
          <MobileFab onClick={openCreate}>+ {t('adminUnits.newUnit')}</MobileFab>
        </Stack>

        <Group gap={16} align="flex-end" wrap="wrap">
          <Box w={{ base: '100%', sm: 280 }}>
            <SearchField
              hideLabel
              placeholder={t('adminUnits.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
          </Box>
        </Group>

        <Box visibleFrom="sm">
          <CardSection>
            {!isLoading && pageItems.length === 0 && (
              <Center py={40}>
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('adminUnits.emptyState')}
                </Text>
              </Center>
            )}

            {pageItems.length > 0 && (
              <Box style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: 640 }}>
                  <div
                    className="table-header-row"
                    style={{
                      ...tableRowStyle,
                      padding: '12px 20px',
                      backgroundColor: 'var(--color-bg-canvas)',
                      borderBottom: '1px solid var(--color-border-soft)'
                    }}
                  >
                    <Text
                      fz={10}
                      fw={600}
                      w={codeColumnWidth}
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                    >
                      {t('adminUnits.colCode')}
                    </Text>
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <Text
                        key={lang.code}
                        fz={10}
                        fw={600}
                        w={nameColumnWidth}
                        style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                      >
                        {lang.label}
                      </Text>
                    ))}
                    <Text
                      fz={10}
                      fw={600}
                      w={actionColumnWidth}
                      ta="right"
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                    >
                      {t('platformAdmin.colAction')}
                    </Text>
                  </div>

                  {pageItems.map((unit, index) => (
                    <div
                      key={unit.id}
                      style={{
                        ...tableRowStyle,
                        padding: '14px 20px',
                        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Group gap={8} wrap="nowrap" w={codeColumnWidth}>
                        <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                          {unit.code}
                        </Text>
                        {!unit.isActive && (
                          <StatusPill
                            label={t('adminUnits.colInactive')}
                            bg="var(--color-status-danger-soft)"
                            fg="var(--color-status-danger)"
                          />
                        )}
                      </Group>
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <Text
                          key={lang.code}
                          fz={13}
                          w={nameColumnWidth}
                          style={{ color: 'var(--color-text-secondary)' }}
                          truncate
                        >
                          {unit.names?.[lang.code] ?? '—'}
                        </Text>
                      ))}
                      <Group w={actionColumnWidth} justify="flex-end">
                        <UnitActionsMenu unit={unit} onEdit={() => openEdit(unit)} />
                      </Group>
                    </div>
                  ))}
                </div>
              </Box>
            )}

            {pageItems.length > 0 && (
              <ListPagination
                label={paginationLabel}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
            )}
          </CardSection>
        </Box>

        <Stack hiddenFrom="sm" gap={12}>
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('adminUnits.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.map((unit) => (
            <Stack
              key={unit.id}
              gap={10}
              p={16}
              style={{
                borderRadius: 12,
                border: '1px solid var(--color-border-soft)',
                backgroundColor: 'var(--color-bg-surface)'
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap={8} wrap="nowrap">
                  <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                    {unit.code}
                  </Text>
                  {!unit.isActive && (
                    <StatusPill
                      label={t('adminUnits.colInactive')}
                      bg="var(--color-status-danger-soft)"
                      fg="var(--color-status-danger)"
                    />
                  )}
                </Group>
                <UnitActionsMenu unit={unit} onEdit={() => openEdit(unit)} />
              </Group>
              <Stack gap={6}>
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <Group key={lang.code} justify="space-between" wrap="nowrap">
                    <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                      {lang.label}
                    </Text>
                    <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                      {unit.names?.[lang.code] ?? '—'}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Stack>
          ))}

          {pageItems.length > 0 && (
            <ListPagination
              label={paginationLabel}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              bordered={false}
            />
          )}
        </Stack>
      </Stack>

      <UnitFormModal
        opened={modalOpened}
        onClose={closeModal}
        unit={editingUnit}
        existingCodes={units.map((u) => u.code).filter((code): code is string => !!code)}
        onSaved={setSavedMessage}
      />
    </Container>
  );
};
